import { valibotResolver } from '@hookform/resolvers/valibot'
import { Input } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { type FormEvent, useCallback, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'

import { type UpdateShipmentOrderHistoryRequest } from '../../api'
import { useUpdateShipmentOrderHistoryMutation } from '../../hooks'
import {
  createDefaultShipmentOrderHistoryFormValue,
  shipmentOrderHistoryFormValueSchema,
  type ShipmentOrderHistoryFormValue
} from '../../models'

type Props = {
  tenantKey: string
  updateRequest: UpdateShipmentOrderHistoryRequest
  onSuccess: (historyId: number) => void
  onClose: () => void
}

export const ShipmentOrderHistoryFormModalContent: FC<Props> = ({
  tenantKey,
  updateRequest,
  onSuccess,
  onClose
}) => {
  const { mutateAsync: updateHistory } = useUpdateShipmentOrderHistoryMutation()
  const formMethods = useForm<ShipmentOrderHistoryFormValue>({
    defaultValues: createDefaultShipmentOrderHistoryFormValue(
      updateRequest.dto.name
    ),
    resolver: valibotResolver(shipmentOrderHistoryFormValueSchema)
  })

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit,
    reset
  } = formMethods

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }

      return handleSubmit(async (formValue) => {
        try {
          await updateHistory({
            tenantKey,
            id: updateRequest.id,
            dto: formValue
          })
          onSuccess(updateRequest.id)
          toast.success(SUCCESS_MESSAGES.UPDATED)
          reset()
        } catch (error) {
          toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_UPDATE)
        }
      })(event)
    },
    [handleSubmit, tenantKey, updateRequest, onSuccess, reset, updateHistory]
  )

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[120px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh>
                <label htmlFor="name">{LABELS.HISTORY_NAME}</label>
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd>
                <Controller
                  control={control}
                  name={'name'}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input {...field} id={field.name} />
                    </FormItem>
                  )}
                />
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          loading={isSubmitting}
          disabled={!isValid}
        >
          {ACTIONS.UPDATE}
        </Button>
      </ModalFooter>
    </form>
  )
}
